import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ACCOUNTS,
  TRADES,
  fmtMoney,
  fmtMoneyExact,
  fmtPct,
  getAccount,
  pnlClass,
  type Trade,
} from '../data/demo'
import { Page } from '../components/Layout'
import {
  AllocationBars,
  ConnectAccountPanel,
  EquityChart,
  Metric,
  MonthlyHeatmap,
  StateBlock,
  useModal,
} from '../components/ui'

type Period = '30D' | '90D' | 'YTD' | 'ALL'
type SortKey = 'closeTime' | 'pnl' | 'symbol' | 'rMultiple'

export function DashboardPage() {
  const modal = useModal()
  const [accountId, setAccountId] = useState(ACCOUNTS[0]!.id)
  const [period, setPeriod] = useState<Period>('ALL')
  const [symbol, setSymbol] = useState('ALL')
  const [side, setSide] = useState<'ALL' | 'long' | 'short'>('ALL')
  const [sortKey, setSortKey] = useState<SortKey>('closeTime')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [feed, setFeed] = useState<'ready' | 'loading' | 'error'>('ready')

  const account = getAccount(accountId)

  const curve = useMemo(() => {
    const c = account.equityCurve
    if (period === 'ALL') return c
    const n = period === '30D' ? 30 : period === '90D' ? 90 : 200
    return c.slice(-n)
  }, [account, period])

  const trades = useMemo(() => {
    let list: Trade[] = accountId === 'grok-alpha' ? [...TRADES] : TRADES.slice(0, 40)
    if (symbol !== 'ALL') list = list.filter((t) => t.symbol === symbol)
    if (side !== 'ALL') list = list.filter((t) => t.side === side)
    list.sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    })
    return list
  }, [accountId, symbol, side, sortKey, sortDir])

  const symbols = useMemo(() => ['ALL', ...new Set(TRADES.map((t) => t.symbol))], [])

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(k)
      setSortDir(k === 'symbol' ? 'asc' : 'desc')
    }
  }

  return (
    <Page wide>
      <div className="app-header">
        <div>
          <div className="chip-row" style={{ marginBottom: 8 }}>
            <span className="badge demo">DEMO</span>
            <span className="badge verified">
              {account.status === 'verified' ? 'Verified' : 'Read-only'}
            </span>
            <span className="badge">Fresh {account.freshnessMinutes}m ago</span>
          </div>
          <h1>Atlas Track</h1>
          <p className="muted" style={{ margin: '0.35rem 0 0' }}>
            Multi-account performance command center · fictional development data · read-only
          </p>
        </div>
        <div className="chip-row">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() =>
              modal.open(<ConnectAccountPanel onClose={modal.close} />, { title: 'Connect account' })
            }
          >
            Connect account
          </button>
          <Link className="btn btn-secondary btn-sm" to={`/proof/${account.id === 'desk-book' || account.id === 'prop-stage' ? 'grok-alpha' : account.id}`}>
            Open proof
          </Link>
        </div>
      </div>

      <div className="toolbar">
        <label>
          <span className="sr-only">Account</span>
          <select
            className="select"
            style={{ width: 'auto', minWidth: 200 }}
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            {ACCOUNTS.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <div className="seg" role="group" aria-label="Period">
          {(['30D', '90D', 'YTD', 'ALL'] as Period[]).map((p) => (
            <button key={p} type="button" aria-pressed={period === p} onClick={() => setPeriod(p)}>
              {p}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setFeed('loading')
            window.setTimeout(() => setFeed(Math.random() > 0.15 ? 'ready' : 'error'), 700)
          }}
        >
          Refresh feed
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFeed('error')}>
          Simulate failure
        </button>
      </div>

      {feed === 'loading' && <StateBlock mode="loading" />}
      {feed === 'error' && <StateBlock mode="error" onRetry={() => setFeed('ready')} />}

      {feed === 'ready' && (
        <>
          <div className="grid-metrics" style={{ marginBottom: '1rem' }}>
            <div className="card card-pad">
              <Metric label="Balance" value={fmtMoney(account.balance)} sub={account.currency} />
            </div>
            <div className="card card-pad">
              <Metric label="Equity" value={fmtMoney(account.equity)} tone="lime" />
            </div>
            <div className="card card-pad">
              <Metric label="Net return" value={fmtPct(account.netReturnPct)} tone="lime" sub="after D/W" />
            </div>
            <div className="card card-pad">
              <Metric
                label="Absolute P&amp;L"
                value={fmtMoney(account.absoluteReturn)}
                tone={account.absoluteReturn >= 0 ? 'lime' : 'danger'}
              />
            </div>
            <div className="card card-pad">
              <Metric label="Max DD" value={fmtPct(-account.maxDrawdownPct)} tone="danger" />
            </div>
            <div className="card card-pad">
              <Metric label="Current DD" value={fmtPct(-account.currentDrawdownPct)} tone="amber" />
            </div>
            <div className="card card-pad">
              <Metric label="Sharpe" value={account.sharpe.toFixed(2)} sub={`Sortino ${account.sortino.toFixed(2)}`} />
            </div>
            <div className="card card-pad">
              <Metric label="Profit factor" value={account.profitFactor.toFixed(2)} />
            </div>
            <div className="card card-pad">
              <Metric label="Win rate" value={`${account.winRate}%`} />
            </div>
            <div className="card card-pad">
              <Metric label="Expectancy" value={fmtMoneyExact(account.expectancy)} />
            </div>
            <div className="card card-pad">
              <Metric
                label="Avg win / loss"
                value={`${fmtMoney(account.avgWin)} / ${fmtMoney(account.avgLoss)}`}
              />
            </div>
            <div className="card card-pad">
              <Metric label="Recovery factor" value={account.recoveryFactor.toFixed(2)} />
            </div>
          </div>

          <div className="dash-grid">
            <div className="stack">
              <div className="card card-pad">
                <div className="cc-top">
                  <h2 className="display" style={{ margin: 0, fontSize: '1.25rem' }}>
                    Balance / equity
                  </h2>
                  <span className="mute mono">
                    {account.platform} · {account.leverage}
                  </span>
                </div>
                <EquityChart data={curve} height={280} />
              </div>

              <div className="card card-pad">
                <div className="toolbar" style={{ marginBottom: 8 }}>
                  <h2 className="display" style={{ margin: 0, fontSize: '1.25rem', flex: 1 }}>
                    Trades
                  </h2>
                  <label>
                    <span className="sr-only">Symbol</span>
                    <select className="select" style={{ width: 'auto' }} value={symbol} onChange={(e) => setSymbol(e.target.value)}>
                      {symbols.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="seg" role="group" aria-label="Side filter">
                    {(['ALL', 'long', 'short'] as const).map((s) => (
                      <button key={s} type="button" aria-pressed={side === s} onClick={() => setSide(s)}>
                        {s === 'ALL' ? 'All' : s}
                      </button>
                    ))}
                  </div>
                </div>
                {trades.length === 0 ? (
                  <StateBlock mode="empty" />
                ) : (
                  <div className="table-wrap">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleSort('closeTime')}>
                              Close {sortKey === 'closeTime' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                            </button>
                          </th>
                          <th>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleSort('symbol')}>
                              Symbol {sortKey === 'symbol' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                            </button>
                          </th>
                          <th>Side</th>
                          <th>Setup</th>
                          <th className="num">
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleSort('rMultiple')}>
                              R {sortKey === 'rMultiple' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                            </button>
                          </th>
                          <th className="num">
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleSort('pnl')}>
                              P&amp;L {sortKey === 'pnl' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {trades.map((t) => (
                          <Fragment key={t.id}>
                            <tr
                              aria-expanded={expanded === t.id}
                              onClick={() => setExpanded((e) => (e === t.id ? null : t.id))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  setExpanded((x) => (x === t.id ? null : t.id))
                                }
                              }}
                              tabIndex={0}
                            >
                              <td className="mono">{t.closeTime.slice(0, 16).replace('T', ' ')}</td>
                              <td>{t.symbol}</td>
                              <td>{t.side}</td>
                              <td>{t.setup}</td>
                              <td className={`num mono ${pnlClass(t.rMultiple)}`}>{t.rMultiple.toFixed(2)}R</td>
                              <td className={`num mono ${pnlClass(t.pnl)}`}>{fmtMoneyExact(t.pnl)}</td>
                            </tr>
                            {expanded === t.id ? (
                              <tr className="trade-detail">
                                <td colSpan={6}>
                                  <div className="split-2" style={{ padding: '0.5rem 0' }}>
                                    <div>
                                      <div className="mute">Notes</div>
                                      <div>{t.notes}</div>
                                      <div className="mute" style={{ marginTop: 8 }}>
                                        Session {t.session} · {t.weekday} · {t.lots} lots
                                      </div>
                                    </div>
                                    <div>
                                      <div className="chip-row">
                                        {t.tags.map((tg) => (
                                          <span key={tg} className="badge">
                                            {tg}
                                          </span>
                                        ))}
                                        {t.mistakes.map((m) => (
                                          <span key={m} className="badge danger">
                                            {m}
                                          </span>
                                        ))}
                                        {t.ruleBreaks.map((m) => (
                                          <span key={m} className="badge warn">
                                            {m}
                                          </span>
                                        ))}
                                      </div>
                                      <div className="mono mute" style={{ marginTop: 8 }}>
                                        Entry {t.entry} → Exit {t.exit} · {t.pips} pips
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ) : null}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="stack">
              <div className="card card-pad">
                <h3 className="display" style={{ marginTop: 0, fontSize: '1.1rem' }}>
                  Deposits &amp; withdrawals
                </h3>
                <div className="grid-metrics">
                  <Metric label="Deposits" value={fmtMoney(account.deposits)} />
                  <Metric label="Withdrawals" value={fmtMoney(account.withdrawals)} />
                  <Metric label="Net external" value={fmtMoney(account.netDeposit)} />
                </div>
                <p className="mute" style={{ marginBottom: 0, fontSize: '0.85rem' }}>
                  Performance excludes external flows. Absolute return is equity − net deposits.
                </p>
              </div>

              <div className="card card-pad">
                <h3 className="display" style={{ marginTop: 0, fontSize: '1.1rem' }}>
                  Monthly return heatmap
                </h3>
                <MonthlyHeatmap data={account.monthly} />
              </div>

              <div className="card card-pad">
                <h3 className="display" style={{ marginTop: 0, fontSize: '1.1rem' }}>
                  Strategy allocation
                </h3>
                <AllocationBars
                  items={account.strategyAllocation.map((s) => ({
                    label: s.name,
                    pct: s.pct,
                    color: s.color,
                  }))}
                />
              </div>

              <div className="card card-pad">
                <h3 className="display" style={{ marginTop: 0, fontSize: '1.1rem' }}>
                  Symbol exposure
                </h3>
                <AllocationBars
                  items={account.symbolExposure.map((s) => ({
                    label: s.symbol,
                    pct: s.pct,
                    detail: fmtMoney(s.pnl),
                    color: s.pnl >= 0 ? undefined : '#ff4d4d',
                  }))}
                />
              </div>

              <div className="card card-pad">
                <h3 className="display" style={{ marginTop: 0, fontSize: '1.1rem' }}>
                  Currency exposure
                </h3>
                <AllocationBars
                  items={account.currencyExposure.map((c) => ({ label: c.ccy, pct: c.pct }))}
                />
              </div>

              <div className="card card-pad">
                <h3 className="display" style={{ marginTop: 0, fontSize: '1.1rem' }}>
                  Long / short
                </h3>
                {account.longShort.map((ls) => (
                  <div key={ls.side} style={{ marginBottom: 8 }}>
                    <strong>{ls.side}</strong>{' '}
                    <span className={`mono ${pnlClass(ls.pnl)}`}>{fmtMoney(ls.pnl)}</span>
                    <div className="mute">
                      WR {ls.winRate}% · {ls.trades} trades
                    </div>
                  </div>
                ))}
              </div>

              <div className="card card-pad">
                <h3 className="display" style={{ marginTop: 0, fontSize: '1.1rem' }}>
                  Session &amp; weekday
                </h3>
                <AllocationBars
                  items={account.sessionPerf.map((s) => ({
                    label: s.session,
                    pct: Math.min(100, Math.abs(s.pnl) / 50),
                    detail: fmtMoney(s.pnl),
                  }))}
                />
                <div style={{ height: 8 }} />
                <AllocationBars
                  items={account.weekdayPerf.map((s) => ({
                    label: s.day,
                    pct: Math.min(100, Math.abs(s.pnl) / 30),
                    detail: fmtMoney(s.pnl),
                  }))}
                />
              </div>

              <div className="card card-pad">
                <h3 className="display" style={{ marginTop: 0, fontSize: '1.1rem' }}>
                  Open risk
                </h3>
                <div className="grid-metrics">
                  <Metric label="Positions" value={String(account.openRisk.positions)} />
                  <Metric label="Notional" value={fmtMoney(account.openRisk.notional)} />
                  <Metric label="Margin used" value={`${account.openRisk.marginUsedPct}%`} />
                  <Metric label="Heat" value={`${Math.round(account.openRisk.heat * 100)}%`} tone="amber" />
                </div>
                <p className="mute" style={{ marginBottom: 0 }}>
                  Largest book: {account.openRisk.largestSymbol}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </Page>
  )
}
